import { Link, useRoute } from "wouter";
import type { ReactNode } from "react";

/**
 * The application shell.
 *
 * A floating pill nav, the wordmark left, one dark button right. design.md's three-radius rhythm is
 * visible in this one component: 200px on the nav container, 0px on the button, and the cards below
 * carry the asymmetric 6px 0px 0px.
 *
 * The mark is served as a transparent PNG from `public/brand` rather than imported, so it stays out
 * of the JavaScript bundle and is cached as its own asset. It carries the ember-orange dot, which is
 * the only saturated colour in the shell and the same token the product uses for a refusal.
 */

const NAV = [
  { href: "/protocol", label: "How it works" },
  { href: "/mandates", label: "Mandates" },
  { href: "/providers", label: "Providers" },
  { href: "/auctions", label: "Selection" },
  { href: "/risk", label: "Risk" },
  { href: "/receipts", label: "Receipts" },
  { href: "/agents", label: "Agents" },
  { href: "/utilization", label: "Utilization" },
  { href: "/evidence", label: "Evidence" },
  { href: "/deployment", label: "Deployment" },
] as const;

function NavLink({ href, label }: { href: string; label: string }) {
  const [isActive] = useRoute(`${href}/*?`);
  return (
    <Link href={href} className="nav-link" aria-current={isActive ? "page" : undefined}>
      {label}
    </Link>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <>
      <header className="surface-canvas" style={{ borderBottom: "1px solid var(--color-mist)" }}>
        <div
          className="page row"
          style={{ justifyContent: "space-between", paddingBlock: "var(--spacing-16)" }}
        >
          <Link
            href="/"
            className="row"
            style={{
              gap: "var(--spacing-8)",
              textDecoration: "none",
              color: "var(--color-graphite)",
              alignItems: "center",
            }}
            aria-label="SYRTY, home"
          >
            <img
              src="/brand/mark-256.png"
              alt=""
              width={26}
              height={24}
              style={{ display: "block" }}
              // Decorative: the wordmark beside it already names the product, so a second
              // announcement would make a screen reader say "SYRTY SYRTY".
              aria-hidden="true"
            />
            <span className="polysans" style={{ fontSize: 18 }}>
              SYRTY
            </span>
          </Link>

          <nav className="nav-pill" aria-label="Main">
            {NAV.map((item) => (
              <NavLink key={item.href} {...item} />
            ))}
          </nav>

          <a className="button-primary" href="/mandates/new">
            Create mandate
          </a>
        </div>
      </header>

      <main>{children}</main>

      <footer className="surface-ash">
        <div className="page section stack" style={{ gap: "var(--spacing-16)" }}>
          <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
            <span className="row" style={{ gap: "var(--spacing-8)", alignItems: "center" }}>
              <img src="/brand/mark-256.png" alt="" width={20} height={18} style={{ display: "block" }} aria-hidden="true" />
              <span className="polysans" style={{ fontSize: 16 }}>
                SYRTY — settlement assurance for programmable money
              </span>
            </span>
            <span className="caption">
              Arc Public Testnet · chain 5042002 · not for production use
            </span>
          </div>
          <hr className="divider" />
          <p className="caption">
            SYRTY does not custody funds beyond per-obligation settlement escrow, does not offer
            insurance, and does not guarantee anything it cannot settle on chain. Where a guarantee
            cannot be met, the protocol reports a shortfall rather than a success.
          </p>
        </div>
      </footer>
    </>
  );
}
